import React, { useState } from 'react';
import { Settings, Calendar, Heart, Brain } from 'lucide-react';
import { usePeriodStore } from '../store/periodStore';
import { CycleCalendar } from '../components/period/CycleCalendar';
import { SymptomTracker } from '../components/period/SymptomTracker';
import { MoodTracker } from '../components/period/MoodTracker';
import { CycleInsights } from '../components/period/CycleInsights';
import { CyclePrediction } from '../components/period/CyclePrediction';
import { RemediesCard } from '../components/period/RemediesCard';
import { BlogsSection } from '../components/period/BlogsSection';
import { SettingsDialog } from '../components/period/SettingsDialog';
import { DragonAiChat } from '../components/period/DragonAiChat';

interface Tab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  { id: 'calendar', label: 'Calendar', icon: <Calendar className="w-4 h-4" /> },
  { id: 'symptoms', label: 'Symptoms', icon: <Heart className="w-4 h-4" /> },
  { id: 'insights', label: 'Insights', icon: <Brain className="w-4 h-4" /> }
];

export function PeriodTrackerPage() {
  const [activeTab, setActiveTab] = useState('calendar');
  const [showSettings, setShowSettings] = useState(false);
  const { currentCycle } = usePeriodStore();

  const renderContent = () => {
    switch (activeTab) {
      case 'calendar':
        return (
          <div className="space-y-6">
            <CycleCalendar />
            <CyclePrediction />
          </div>
        );
      case 'symptoms':
        return (
          <div className="space-y-6">
            <SymptomTracker />
            <MoodTracker />
            <RemediesCard />
          </div>
        );
      case 'insights':
        return (
          <div className="space-y-6">
            <CycleInsights />
            <BlogsSection />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Enhanced Gradient Background - More soothing colors */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-purple-400/5 to-background"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-pink-300/10 via-purple-300/5 to-transparent"></div>
        <div className="absolute inset-0 bg-[conic-gradient(from_90deg_at_80%_50%,_var(--tw-gradient-stops))] from-primary/5 via-purple-400/5 to-transparent opacity-30"></div>
        <div className="absolute inset-0 backdrop-blur-[100px]"></div>
      </div>

      <div className="relative">
        {/* Header with Softer Colors - Fixed z-index to be below main header */}
        <div className="sticky top-[72px] z-30 bg-background/60 backdrop-blur-xl border-b border-primary/10">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Cycle
                </h1>
                <p className="text-sm text-primary/80">
                  Day {currentCycle?.currentDay || 1} • {currentCycle?.phase || 'Follicular Phase'}
                </p>
              </div>
              <button 
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-full hover:bg-primary/10 transition-colors"
              >
                <Settings className="w-5 h-5 text-primary" />
              </button>
            </div>

            {/* Enhanced Tab Navigation with Softer Colors */}
            <div className="flex gap-2 mt-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-primary/90 via-purple-400/90 to-pink-400/90 text-primary-foreground shadow-lg shadow-primary/20'
                      : 'text-primary/60 hover:bg-primary/10'
                  }`}
                >
                  {tab.icon}
                  <span className="text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-6">
          <div className="space-y-6">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      {showSettings && (
        <SettingsDialog onClose={() => setShowSettings(false)} />
      )}

      {/* AI Chat Assistant */}
      <DragonAiChat />
    </div>
  );
}